// useAmbientAudio: plays either an HTML5 audio URL or a synthesized ambient
// soundscape via Web Audio API, exposes a master gain for fade-outs.
import { useEffect, useRef, useState, useCallback } from 'react';

export default function useAmbientAudio({ url, mixer } = {}) {
  const ctxRef = useRef(null);
  const masterRef = useRef(null);
  const audioElRef = useRef(null);
  const audioSrcRef = useRef(null);
  const synthNodesRef = useRef([]);
  const mixerNodesRef = useRef({});
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);

  const ensureCtx = useCallback(() => {
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctxRef.current = new AC();
      const g = ctxRef.current.createGain();
      g.gain.value = 0.9;
      g.connect(ctxRef.current.destination);
      masterRef.current = g;
    }
    return ctxRef.current;
  }, []);

  const buildSynth = useCallback(() => {
    const ctx = ensureCtx();
    // Pink-ish noise via biquad-filtered white noise
    const bufferSize = 2 * ctx.sampleRate;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let b0 = 0, b1 = 0, b2 = 0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99765 * b0 + white * 0.099046;
      b1 = 0.96300 * b1 + white * 0.2965164;
      b2 = 0.57000 * b2 + white * 1.0526913;
      data[i] = (b0 + b1 + b2 + white * 0.1848) * 0.11;
    }
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    noise.loop = true;
    const nGain = ctx.createGain();
    nGain.gain.value = 0.25;
    const lowpass = ctx.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = 900;
    noise.connect(lowpass).connect(nGain).connect(masterRef.current);
    noise.start();

    // Slow amplitude modulation for gentle breath-like waves
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.value = 0.08;
    lfoGain.gain.value = 0.08;
    lfo.connect(lfoGain).connect(nGain.gain);
    lfo.start();

    // Two soft sine drones (root + fifth) for warmth
    const droneA = ctx.createOscillator();
    droneA.frequency.value = 110; // A2
    const dgA = ctx.createGain(); dgA.gain.value = 0.02;
    droneA.connect(dgA).connect(masterRef.current);
    droneA.start();
    const droneB = ctx.createOscillator();
    droneB.frequency.value = 164.81; // E3
    const dgB = ctx.createGain(); dgB.gain.value = 0.015;
    droneB.connect(dgB).connect(masterRef.current);
    droneB.start();

    synthNodesRef.current = [noise, lfo, droneA, droneB];
  }, [ensureCtx]);

  const teardownSynth = useCallback(() => {
    synthNodesRef.current.forEach((n) => { try { n.stop(); } catch (_e) { /* ignore */ } });
    synthNodesRef.current = [];
  }, []);

  const play = useCallback(async () => {
    const ctx = ensureCtx();
    if (ctx.state === 'suspended') await ctx.resume();
    // Reset gain (in case fade-out killed it)
    if (masterRef.current) {
      masterRef.current.gain.cancelScheduledValues(ctx.currentTime);
      masterRef.current.gain.setValueAtTime(0.9, ctx.currentTime);
    }
    if (url) {
      if (!audioElRef.current) {
        const el = new Audio(url);
        el.loop = true;
        el.crossOrigin = 'anonymous';
        audioElRef.current = el;
        try {
          const src = ctx.createMediaElementSource(el);
          src.connect(masterRef.current);
          audioSrcRef.current = src;
        } catch (_e) {
          // fallback: connect to destination directly
        }
      }
      try { await audioElRef.current.play(); } catch (_e) { /* ignore */ }
    } else {
      if (!synthNodesRef.current.length) buildSynth();
    }
    setPlaying(true);
    setReady(true);
  }, [ensureCtx, buildSynth, url]);

  const pause = useCallback(() => {
    if (audioElRef.current) { try { audioElRef.current.pause(); } catch (_e) { /* ignore */ } }
    teardownSynth();
    setPlaying(false);
  }, [teardownSynth]);

  const fadeOutAndStop = useCallback((durationSec = 15) => {
    const ctx = ctxRef.current;
    if (!ctx || !masterRef.current) { pause(); return; }
    const now = ctx.currentTime;
    masterRef.current.gain.cancelScheduledValues(now);
    masterRef.current.gain.setValueAtTime(masterRef.current.gain.value, now);
    masterRef.current.gain.exponentialRampToValueAtTime(0.0001, now + durationSec);
    setTimeout(() => pause(), durationSec * 1000 + 100);
  }, [pause]);

  // Ambient mixer channels
  useEffect(() => {
    if (!playing || !mixer) return;
    const ctx = ensureCtx();
    const specs = {
      rain: { freq: 3200, gainMax: 0.35 },
      ocean: { freq: 500, gainMax: 0.3 },
      forest: { freq: 2200, gainMax: 0.25 },
      fire: { freq: 800, gainMax: 0.3 },
      wind: { freq: 1400, gainMax: 0.3 },
    };
    Object.entries(mixer).forEach(([id, vol]) => {
      const v = (vol || 0) / 100;
      const spec = specs[id];
      if (!spec) return;
      let node = mixerNodesRef.current[id];
      if (v <= 0) {
        if (node) {
          try { node.source.stop(); } catch (_e) { /* ignore */ }
          delete mixerNodesRef.current[id];
        }
        return;
      }
      if (!node) {
        const bufferSize = 2 * ctx.sampleRate;
        const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
        const source = ctx.createBufferSource();
        source.buffer = buffer; source.loop = true;
        const filt = ctx.createBiquadFilter();
        filt.type = 'bandpass'; filt.frequency.value = spec.freq; filt.Q.value = 1.2;
        const g = ctx.createGain(); g.gain.value = v * spec.gainMax;
        source.connect(filt).connect(g).connect(masterRef.current);
        source.start();
        node = { source, gain: g, spec };
        mixerNodesRef.current[id] = node;
      } else {
        node.gain.gain.setTargetAtTime(v * node.spec.gainMax, ctx.currentTime, 0.2);
      }
    });
  }, [mixer, playing, ensureCtx]);

  useEffect(() => () => {
    // full cleanup on unmount
    try { audioElRef.current && audioElRef.current.pause(); } catch (_e) { /* ignore */ }
    teardownSynth();
    Object.values(mixerNodesRef.current).forEach((n) => { try { n.source.stop(); } catch (_e) { /* ignore */ } });
    mixerNodesRef.current = {};
    try { ctxRef.current && ctxRef.current.close(); } catch (_e) { /* ignore */ }
    ctxRef.current = null;
  }, [teardownSynth]);

  return { play, pause, fadeOutAndStop, playing, ready };
}
